import type { CurrencyEnum, CreateOrderQuoteOfferBatchSampleInput } from '@codezo/graphql';
import { SamplePaymentTreatment } from '@codezo/graphql';
import { CurrencyText } from '../../../CurrencyText';
import { CurrencyInput } from '../../../ui/currency-input';
import { NumberInput } from '../../../ui/number-input';
import { Textarea } from '../../../ui/textarea';
import { SampleTreatmentPicker } from '../../../SampleTreatmentPicker';
import type { SampleInput } from './types';
import {
	SampleCatalogPicker,
	type CatalogSampleRow,
	type SampleFill,
} from '../../../pricing/SampleCatalogPicker';

interface SampleTabProps {
	sample: SampleInput | undefined;
	updateSample: (fields: Partial<CreateOrderQuoteOfferBatchSampleInput>) => void;
	currency: CurrencyEnum;
	/** Every catalogue sample price on the product. */
	catalogSamples?: CatalogSampleRow[];
	/** The offer's supplier, which decides which catalogue prices are usable. */
	assignedVetterId?: string | null;
	onPickCatalog?: (fill: SampleFill) => void;
}

export function SampleTab({
	sample,
	updateSample,
	currency,
	catalogSamples = [],
	assignedVetterId,
	onPickCatalog,
}: SampleTabProps) {
	const s = sample;

	/* ── Sample total preview ── the sample and its freight, which is all there
	 * is to charge. The admin fee that used to be a third term is gone: the price
	 * below is entered whole rather than marked up on the way out. */
	const samplePrice = s?.baseSamplePrice ?? 0;
	const shippingPrice = s?.baseShippingPrice ?? 0;
	const sampleTotal = samplePrice + shippingPrice;
	const hasSampleTotal = samplePrice > 0 || shippingPrice > 0;
	const isWaived = s?.paymentTreatment === SamplePaymentTreatment.Waived;

	return (
		<div className="flex flex-col gap-3">
			{/* Beside the total rather than altering it: CHARGED and PREPAID quote
			    the client the same figure, and only what happens afterwards differs. */}
			<SampleTreatmentPicker
				label="Sample payment"
				value={s?.paymentTreatment ?? SamplePaymentTreatment.Prepaid}
				onChange={(paymentTreatment) => updateSample({ paymentTreatment })}
			/>

			{!isWaived && onPickCatalog && (
				<SampleCatalogPicker
					rows={catalogSamples}
					assignedVetterId={assignedVetterId}
					formCurrency={currency}
					current={{
						samplePrice: s?.baseSamplePrice ?? null,
						shippingPrice: s?.baseShippingPrice ?? null,
					}}
					onPick={onPickCatalog}
				/>
			)}

			{/* A waived sample has no price to set: the price maps feed exactly one
			    thing — what the client pays — and nobody is paying. */}
			{!isWaived && (
				<div className="grid grid-cols-2 gap-2">
					<div className="flex flex-col gap-1">
						<span className="text-2xs text-text-4">Sample price</span>
						<CurrencyInput
							currency={currency}
							value={s?.baseSamplePrice ? String(s.baseSamplePrice) : ''}
							onChange={(e) =>
								updateSample({
									baseSamplePrice:
										parseFloat((e.target as HTMLInputElement).value) || 0,
								})
							}
							placeholder="0.00"
							min={0}
							step="any"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-2xs text-text-4">Shipping price</span>
						<CurrencyInput
							currency={currency}
							value={s?.baseShippingPrice ? String(s.baseShippingPrice) : ''}
							onChange={(e) =>
								updateSample({
									baseShippingPrice:
										parseFloat((e.target as HTMLInputElement).value) ||
										undefined,
								})
							}
							placeholder="0.00"
							min={0}
							step="any"
						/>
					</div>
				</div>
			)}

			{/* Only when there is something to charge. A waived sample has no
		    prices to sum, and totalPrice now returns 0 server-side. */}
			{!isWaived && hasSampleTotal && (
				<div className="flex items-center justify-between rounded-lg border border-line-1 bg-surface-fill/50 px-3 py-2">
					<div className="flex flex-col">
						<span className="text-2xs text-text-4">Sample total</span>
						<span className="text-2xs text-text-4">Sample + shipping</span>
					</div>
					<CurrencyText
						amount={sampleTotal}
						currency={currency}
						className="text-base font-semibold text-text-1"
					/>
				</div>
			)}

			<div className="grid grid-cols-2 gap-2">
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Min production days</span>
					<NumberInput
						value={s?.minProductionDays != null ? String(s.minProductionDays) : ''}
						onChange={(e) =>
							updateSample({
								minProductionDays:
									parseInt((e.target as HTMLInputElement).value) || undefined,
							})
						}
						placeholder="—"
						min={0}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Max production days</span>
					<NumberInput
						value={s?.maxProductionDays != null ? String(s.maxProductionDays) : ''}
						onChange={(e) =>
							updateSample({
								maxProductionDays:
									parseInt((e.target as HTMLInputElement).value) || undefined,
							})
						}
						placeholder="—"
						min={0}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Min delivery days</span>
					<NumberInput
						value={s?.deliveryDaysMin != null ? String(s.deliveryDaysMin) : ''}
						onChange={(e) =>
							updateSample({
								deliveryDaysMin:
									parseInt((e.target as HTMLInputElement).value) || undefined,
							})
						}
						placeholder="—"
						min={0}
					/>
				</div>
				<div className="flex flex-col gap-1">
					<span className="text-2xs text-text-4">Max delivery days</span>
					<NumberInput
						value={s?.deliveryDaysMax != null ? String(s.deliveryDaysMax) : ''}
						onChange={(e) =>
							updateSample({
								deliveryDaysMax:
									parseInt((e.target as HTMLInputElement).value) || undefined,
							})
						}
						placeholder="—"
						min={0}
					/>
				</div>
			</div>
			<div className="flex flex-col gap-1">
				<span className="text-2xs text-text-4">Notes</span>
				<Textarea
					value={s?.notes ?? ''}
					onChange={(e) => updateSample({ notes: e.target.value || undefined })}
					placeholder="Optional notes about the sample"
					className="min-h-15"
					rows={2}
				/>
			</div>
		</div>
	);
}
